import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import { Circle, GoogleMap, Polygon, StandaloneSearchBox } from '@react-google-maps/api';
import {
  Button,
  constants,
  Label,
  postGISToGoogleMapsPoints,
  Tooltip,
} from '@trucktrax/trucktrax-common';
import cx from 'classnames';
import { uniqueId } from 'lodash';
import concat from 'lodash/concat';
import React, { Component, useEffect, useState } from 'react';

import {
  GEOZONE_SHAPE_STYLE,
  GEOZONE_TYPE,
  PLANT_NO_ACTIVATION,
  PLANT_NO_GEOZONES,
} from '../../../constants/appConstants';
import { CARD_MAP_HEIGHT } from '../../../constants/mapConstants';
import { ZoneType } from '../../../types';
import {
  GeocoderResponseWithStatus,
  getAddressGeocode,
  removeAutocomplete,
} from '../../../util/adminUtil';
import { noop } from '../../../util/appUtil';
import recordLog, { LogLevel } from '../../../util/errorUtil';
import { createIconElement } from '../../geotrax/map/markers/iconUtils';
import { CustomDrawingManager, useAdvancedMarker } from '../../geotrax/map/TraxMapDrawingManager';
import styles from './CardMap.module.css';

// removed unused icons import
/**
 * Intended use
 * Displaying and updating a map with single address/marker location (center)
 * with multiple geozones (circle or polygon).
 */

type ExtendedPolygon = (google.maps.Polygon | {}) & {
  [zoneName: string]: google.maps.Polygon | null;
};
type ExtendedCircle = (google.maps.Circle | {}) & { [zoneName: string]: google.maps.Circle | null };
type MapChangeEvent = {
  location?: Location;
  geozones?: Geozones;
};

type ShapeStyle = {
  fillColor: string;
  strokeColor: string;
  fillOpacity: number;
  strokeWeight: number;
  zIndex: number;
  clickable: boolean;
};

function AdvancedMarkerWrapper({
  map,
  location,
  showMarker,
  isEditable,
  selectedZone,
  onChange,
  onLoad,
}: {
  map?: google.maps.Map;
  location?: Location;
  showMarker?: boolean;
  isEditable?: boolean;
  selectedZone?: ZoneType;
  onChange: (change: MapChangeEvent) => void;
  onLoad: () => void;
}) {
  // Create custom flag marker content
  const flagContent = React.useMemo(() => {
    const div = document.createElement('div');
    const flagConfig = {
      path: 'M5 30 L5 5 L20 5 L15 10 L20 15 L5 15 Z',
      fillColor: '#4A5568',
      strokeColor: '#000',
      strokeWeight: 2,
      scale: 1,
    };
    const iconElement = createIconElement(flagConfig, 1);
    if (iconElement) {
      iconElement.style.width = '24px';
      iconElement.style.height = '30px';
      (iconElement as SVGElement).setAttribute?.('viewBox', '0 0 24 30');
      (iconElement as SVGElement)
        .querySelector?.('path')
        ?.setAttribute('shape-rendering', 'geometricPrecision');
      div.appendChild(iconElement);
    }
    return div;
  }, []);

  const position = location
    ? {
        lat: location.latitude,
        lng: location.longitude,
      }
    : undefined;

  const editable = isEditable && selectedZone === GEOZONE_TYPE.PLANT;

  const marker = useAdvancedMarker({
    map,
    position: position || { lat: 0, lng: 0 },
    content: flagContent,
    draggable: editable,
    visible: showMarker && !!position,
    onDragEnd: (event: google.maps.LatLngLiteral) => {
      onChange({
        location: {
          latitude: event.lat ?? 0,
          longitude: event.lng ?? 0,
        },
      });
    },
  });

  useEffect(() => {
    if (marker) {
      onLoad();
    }
  }, [marker, onLoad]);

  return null;
}

export class Map extends Component<MapProps> {
  static defaultProps: Partial<MapProps> = {
    showSearchBox: true,
    showDrawingManager: true,
    isEditable: false,
    showMarker: true,
    onChange: () => {},
    defaultMapZoom: 15,
    selectedZone: undefined,
    mapContainerStyle: {
      height: CARD_MAP_HEIGHT,
      maxWidth: '100%',
    },
  };

  state = {
    isMapHasBeenBounded: false,
  };

  circleRef?: ExtendedCircle;

  polygonRef?: ExtendedPolygon;

  mapRef?: google.maps.Map;

  searchBoxRef?: google.maps.places.SearchBox;

  listenersRef?: {
    [zoneType: string]: google.maps.MapsEventListener[];
  };

  markerWrapperRef = React.createRef<HTMLDivElement>();

  componentDidMount() {
    if (
      this.props.address &&
      (!this.props.location ||
        (this.props.location.latitude === 0 && this.props.location.longitude === 0))
    ) {
      getAddressGeocode(this.props.address, this.onGeocodeSuccess);
    } else {
      this.onChange({
        location: this.props.location,
      });
    }
    removeAutocomplete();
  }

  async componentDidUpdate() {
    if (!this.props.isEditable) {
      this.zoomToBounds();
    }
  }

  componentWillUnmount() {
    this.circleRef = {};
    this.polygonRef = {};
  }

  hasGeozone = () => {
    const keys = concat(
      this.circleRef ? Object.keys(this.circleRef) : [],
      this.polygonRef ? Object.keys(this.polygonRef) : [],
    );

    const zoneNames = ['plant', 'return', 'inqueue'];
    return zoneNames.some(zone => keys.includes(zone));
  };

  zoomToBounds = () => {
    if (!this.hasGeozone()) return;
    const bounds = new google.maps.LatLngBounds();
    const { location } = this.props;

    if (location && location.latitude !== 0 && location.longitude !== 0) {
      bounds.extend(new google.maps.LatLng(location.latitude, location.longitude));
    }

    if (this.circleRef) {
      Object.values(this.circleRef).forEach(circle => {
        if (circle) {
          bounds.union(circle.getBounds()!);
        }
      });
    }

    if (this.polygonRef) {
      const allPoints: google.maps.LatLng[] = [];

      Object.values(this.polygonRef).forEach(polygon => {
        if (polygon) {
          const pathArray = polygon.getPath().getArray();
          allPoints.push(...pathArray);
        }
      });

      if (allPoints.length > 0) {
        allPoints.forEach(point => {
          bounds.extend(point);
        });
      }
    }

    if (this.mapRef?.fitBounds) this.mapRef.fitBounds(bounds);
  };

  onChange = (change: MapChangeEvent) => {
    this.props.onChange!(change);
  };

  onSearchBoxUpdate = () => {
    const places = this.searchBoxRef!.getPlaces() ?? [];
    const bounds = new google.maps.LatLngBounds();

    places.forEach(place => {
      const { location } = place.geometry!;
      if (place.geometry?.viewport) {
        bounds.union(place.geometry.viewport);
      } else {
        bounds.extend(place.geometry!.location!);
      }

      this.onChange({
        location: {
          latitude: location?.lat() ?? 0,
          longitude: location?.lng() ?? 0,
        },
      });
    });
    if (this.mapRef) this.mapRef.fitBounds(bounds);
  };

  // Custom drawing handlers for the new drawing manager
  onMarkerPlaced = (position: google.maps.LatLngLiteral) => {
    this.onChange({
      location: {
        latitude: position.lat,
        longitude: position.lng,
      },
    });
  };

  onCircleDrawingComplete = (circleData: { center: google.maps.LatLngLiteral; radius: number }) => {
    const geozones: { [zoneName: string]: GeozoneInfo } = {};
    Object.keys(this.props.geozones!).forEach(key => {
      const zoneType = key as ZoneType;
      if (zoneType === this.props.selectedZone) {
        geozones[key] = {
          name: key,
          zone: {
            circle: {
              center: {
                latitude: circleData.center.lat,
                longitude: circleData.center.lng,
              },
              radius: circleData.radius,
            },
            polygon: undefined,
          },
        };
      } else {
        const geoZone = this.props.geozones![key as keyof Geozones];
        geozones[key] = {
          name: key,
          zone: { ...geoZone.zone },
        };
      }
    });

    const zones = geozones as Geozones;
    this.onChange({ geozones: zones });
  };

  onPolygonDrawingComplete = (polygonData: { points: google.maps.LatLngLiteral[] }) => {
    const geozones: { [zoneName: string]: GeozoneInfo } = {};
    Object.keys(this.props.geozones!).forEach(key => {
      if (key === this.props.selectedZone!) {
        geozones[key] = {
          name: key,
          zone: {
            circle: undefined,
            polygon: {
              points: Map.googleMapsToPostGISPoints(polygonData.points) ?? [],
            },
          },
        };
      } else {
        const geoZone = this.props.geozones![key as keyof Geozones];
        geozones[key] = {
          name: key,
          zone: { ...geoZone.zone },
        };
      }
    });
    const zones = geozones as Geozones;
    this.onChange({ geozones: zones });
  };

  onCircleComplete = (circle: google.maps.Circle) => {
    const geozones: { [zoneName: string]: GeozoneInfo } = {};
    Object.keys(this.props.geozones!).forEach(key => {
      const zoneType = key as ZoneType;
      if (zoneType === this.props.selectedZone) {
        geozones[key] = {
          name: key,
          zone: {
            circle: {
              center: {
                latitude: circle.getCenter()?.lat() ?? 0,
                longitude: circle.getCenter()?.lng() ?? 0,
              },
              radius: circle.getRadius(),
            },
            polygon: undefined,
          },
        };
      } else {
        const geoZone = this.props.geozones![key as keyof Geozones];
        geozones[key] = {
          name: key,
          zone: { ...geoZone.zone },
        };
      }
    });

    const zones = geozones as Geozones;
    this.onChange({ geozones: zones });
  };

  onPolygonComplete = (polygon: google.maps.Polygon) => {
    const newPolygon = polygon
      .getPath()
      .getArray()
      .map(point => ({
        lat: point.lat() as number,
        lng: point.lng() as number,
      }));

    const geozones: { [zoneName: string]: GeozoneInfo } = {};
    Object.keys(this.props.geozones!).forEach(key => {
      if (key === this.props.selectedZone!) {
        geozones[key] = {
          name: key,
          zone: {
            circle: undefined,
            polygon: {
              points: Map.googleMapsToPostGISPoints(newPolygon) ?? [],
            },
          },
        };
      } else {
        const geoZone = this.props.geozones![key as keyof Geozones];
        geozones[key] = {
          name: key,
          zone: { ...geoZone.zone },
        };
      }
    });
    const zones = geozones as Geozones;
    this.onChange({ geozones: zones });
  };

  onGeocodeSuccess = (json: google.maps.GeocoderResponse) => {
    const response = json as GeocoderResponseWithStatus;

    // Check status first
    if (response.status !== google.maps.GeocoderStatus.OK) {
      recordLog(LogLevel.Error, 'Geocoding failed', {
        status: response.status,
        error_message: response.error_message,
        address: this.props.address,
      });
      // Keep existing location if geocoding fails
      if (this.props.location) {
        this.onChange({
          location: this.props.location,
        });
      }
      return;
    }

    const bounds = new google.maps.LatLngBounds();
    const place = json.results[0];

    if (place) {
      const { location } = place.geometry;

      Object.values(place.geometry.viewport).forEach(v => {
        bounds.extend(v);
      });

      this.onChange({
        location: {
          latitude: location.lat(),
          longitude: location.lng(),
        },
      });
    } else if (this.props.location) {
      this.onChange({
        location: this.props.location,
      });
    }

    this.zoomToBounds();
  };

  onMarkerLoad = () => {
    this.zoomToBounds();
  };

  onPolygonChanged = () => {
    this.onPolygonComplete(this.polygonRef![this.props.selectedZone!]!);
  };

  onCircleRadiusChanged = (zoneType: ZoneType) => {
    if (!this.circleRef || !this.circleRef[this.props.selectedZone!]) return;
    const oldCircleRadius = this.props.geozones![this.props.selectedZone!]!.zone.circle!.radius;
    const newCircleRadius = this.circleRef![this.props.selectedZone!]!.getRadius();
    if (oldCircleRadius !== newCircleRadius && zoneType === this.props.selectedZone) {
      this.onCircleComplete(this.circleRef[this.props.selectedZone!]!);
    }
  };

  onCircleCenterChanged = (zoneType: ZoneType) => {
    if (!this.circleRef || !this.circleRef[this.props.selectedZone!]) return;
    const oldLatitude =
      this.props.geozones![this.props.selectedZone!]!.zone.circle!.center.latitude;
    const newLatitude = this.circleRef![this.props.selectedZone!]!.getCenter()?.lat();
    const oldLongitude =
      this.props.geozones![this.props.selectedZone!]!.zone.circle!.center.longitude;
    const newLongitude = this.circleRef[this.props.selectedZone!]!.getCenter()?.lng();
    if (
      (oldLatitude !== newLatitude || oldLongitude !== newLongitude) &&
      zoneType === this.props.selectedZone
    ) {
      this.onCircleComplete(this.circleRef[this.props.selectedZone!]!);
    }
  };

  getCenter() {
    return this.props.location
      ? {
          lat: this.props.location.latitude,
          lng: this.props.location.longitude,
        }
      : {
          lat: 0,
          lng: 0,
        };
  }

  onPolygonLoad = (polygon: google.maps.Polygon, zoneType: ZoneType) => {
    if (!this.polygonRef) {
      this.polygonRef = {};
    }
    if (!this.listenersRef) {
      this.listenersRef = {};
    }
    if (!this.listenersRef[zoneType]) {
      this.listenersRef[zoneType] = [];
    }

    const path = polygon.getPath();
    this.listenersRef[zoneType].forEach(lis => lis.remove());
    this.listenersRef[zoneType].push(
      path.addListener('set_at', this.onPolygonChanged),
      path.addListener('insert_at', this.onPolygonChanged),
      path.addListener('remove_at', this.onPolygonChanged),
    );

    this.polygonRef[zoneType] = polygon;
    if (!this.state.isMapHasBeenBounded) {
      this.setState({ isMapHasBeenBounded: true }, () => this.zoomToBounds());
    }
  };

  onPolygonUnmount = (polygon: google.maps.Polygon, zoneType: ZoneType) => {
    this.polygonRef![zoneType] = null;
    if (this.listenersRef && this.listenersRef[zoneType]) {
      this.listenersRef[zoneType].forEach(lis => lis.remove());
    }
    google.maps.event.clearInstanceListeners(polygon);
    polygon.setMap(null);
  };

  onCircleLoad = (circle: google.maps.Circle, zoneType: ZoneType) => {
    if (!this.circleRef) {
      this.circleRef = {};
    }

    this.circleRef![zoneType] = circle;
    circle.setMap(this.mapRef!);
    if (!this.state.isMapHasBeenBounded) {
      this.setState({ isMapHasBeenBounded: true }, () => this.zoomToBounds());
    }
  };

  onCircleUnmount = (circle: google.maps.Circle, zoneType: ZoneType) => {
    if (this.circleRef) {
      this.circleRef[zoneType] = null;
    }
    google.maps.event.clearInstanceListeners(circle);
    circle.setMap(null);
  };

  getSearchBoxRef = (el: google.maps.places.SearchBox) => {
    this.searchBoxRef = el;
  };

  onMapLoad = (map: google.maps.Map) => {
    this.mapRef = map;
    if (!this.circleRef) {
      this.circleRef = {};
    }
    if (!this.polygonRef) {
      this.polygonRef = {};
    }
    this.zoomToBounds();
  };

  onMapUnmount = () => {
    this.circleRef = {};
    this.polygonRef = {};
  };

  searchBox() {
    const canSearch = this.props.showSearchBox && this.props.isEditable;
    if (!canSearch) {
      return null;
    }

    return (
      <StandaloneSearchBox onLoad={this.getSearchBoxRef} onPlacesChanged={this.onSearchBoxUpdate}>
        <input
          type="text"
          placeholder="Type address for marker"
          className={cx('tt-input', styles.mapInput)}
          onFocus={removeAutocomplete}
          style={{
            width: '240px',
            height: '32px',
            margin: '5px',
            textOverflow: 'ellipses',
            position: 'absolute',
          }}
        />
      </StandaloneSearchBox>
    );
  }

  static getGeofenceStyle = (zoneType?: ZoneType) => {
    const shapeStyles: ShapeStyle = {
      fillColor: '#67B4F6',
      strokeColor: '#67B4F6',
      fillOpacity: 0.25,
      strokeWeight: 2,
      zIndex: 1,
      clickable: false,
    };

    type GeozoneStyleType = typeof GEOZONE_SHAPE_STYLE;
    const zone: { [zoneName: string]: Partial<ShapeStyle> } = {};
    zone.plant = {
      ...GEOZONE_SHAPE_STYLE[GEOZONE_TYPE.PLANT as keyof GeozoneStyleType],
      zIndex: 3,
    };
    zone.inqueue = {
      ...GEOZONE_SHAPE_STYLE[GEOZONE_TYPE.INQUEUE as keyof GeozoneStyleType],
      zIndex: 2,
    };
    zone.return = {
      ...GEOZONE_SHAPE_STYLE[GEOZONE_TYPE.RETURN as keyof GeozoneStyleType],
      zIndex: 1,
    };

    return zoneType ? { ...shapeStyles, ...zone[zoneType] } : shapeStyles;
  };

  static googleMapsToPostGISPoints = (points?: google.maps.LatLngLiteral[]) => {
    if (!points) return null;
    const pointsCopy = [...points];
    pointsCopy.push(points[0]);
    return pointsCopy.map(point => ({
      latitude: point.lat ?? 0,
      longitude: point.lng ?? 0,
    }));
  };

  getZoneShape = (zoneType: ZoneType, value: GeozoneInfo) => {
    if (!value || !value.zone || !zoneType) return null;

    const editable = this.props.isEditable && this.props.selectedZone === zoneType;
    const options = Map.getGeofenceStyle(zoneType);
    const { zone } = value;
    if (zone.circle) {
      return (
        <Circle
          key={`circle_${zoneType}`}
          center={{
            lat: zone.circle.center.latitude,
            lng: zone.circle.center.longitude,
          }}
          radius={zone.circle.radius}
          onRadiusChanged={() => this.onCircleRadiusChanged(zoneType)}
          onCenterChanged={() => this.onCircleCenterChanged(zoneType)}
          onLoad={e => this.onCircleLoad(e, zoneType)}
          onUnmount={e => this.onCircleUnmount(e, zoneType)}
          options={{
            ...options,
            editable,
            draggable: editable,
            clickable: editable,
          }}
        />
      );
    }
    if (zone.polygon) {
      return (
        <Polygon
          key={`polygon_${zoneType}`}
          path={postGISToGoogleMapsPoints(zone.polygon.points) as any}
          onLoad={e => this.onPolygonLoad(e, zoneType)}
          onUnmount={e => this.onPolygonUnmount(e, zoneType)}
          onDragEnd={this.onPolygonChanged}
          onMouseUp={this.onPolygonChanged}
          options={{ ...options, editable }}
        />
      );
    }

    return null;
  };

  shapes = () => {
    if (!this.props.geozones) return null;

    const { geozones } = this.props;
    const shapeComponents: JSX.Element[] = [];

    Object.values(GEOZONE_TYPE).forEach(zoneType => {
      const shape = this.getZoneShape(zoneType as ZoneType, geozones[zoneType as ZoneType]);
      if (shape) {
        shapeComponents.push(shape);
      }
    });

    return shapeComponents;
  };

  render() {
    const containerStyle = {
      width: '100%',
      maxWidth: this.props.mapContainerStyle!.maxWidth,
      minHeight: this.props.mapContainerStyle!.minHeight,
      height: this.props.mapContainerStyle!.height,
    };

    return (
      <GoogleMap
        zoom={this.props.defaultMapZoom}
        mapContainerStyle={containerStyle}
        center={this.mapRef?.getCenter() || this.getCenter()}
        onLoad={this.onMapLoad}
        onUnmount={this.onMapUnmount}
        options={{
          draggable: this.props.isEditable,
          streetViewControl: false,
          scaleControl: false,
          mapTypeControl: true,
          controlSize: constants.GM_CONTROLSIZE,
          mapId: process.env.REACT_APP_GOOGLE_MAP_ID || 'DEMO_MAP_ID',
          mapTypeControlOptions: {
            mapTypeIds: [google.maps.MapTypeId.ROADMAP, google.maps.MapTypeId.SATELLITE],
            position: google.maps.ControlPosition.LEFT_BOTTOM,
          },
          panControl: false,
          zoomControl: true,
          rotateControl: false,
          fullscreenControl: false,
        }}
      >
        {this.searchBox()}

        <CustomDrawingManager
          map={this.mapRef}
          onMarkerComplete={this.onMarkerPlaced}
          onCircleComplete={this.onCircleDrawingComplete}
          onPolygonComplete={this.onPolygonDrawingComplete}
          isEditable={this.props.isEditable}
          showControls={this.props.showDrawingManager}
          controlPosition={
            this.props.showSearchBox
              ? google.maps.ControlPosition.TOP_RIGHT
              : google.maps.ControlPosition.TOP_CENTER
          }
        />

        <div ref={this.markerWrapperRef}>
          <AdvancedMarkerWrapper
            map={this.mapRef}
            location={this.props.location}
            showMarker={this.props.showMarker}
            isEditable={this.props.isEditable}
            selectedZone={this.props.selectedZone}
            onChange={this.onChange}
            onLoad={this.onMarkerLoad}
          />
        </div>
        {this.shapes()}
      </GoogleMap>
    );
  }
}

type Geozones = {
  plant: GeozoneInfo;
  inqueue: GeozoneInfo;
  return: GeozoneInfo;
};

export interface MapProps {
  location?: Location;
  geozones?: Geozones;
  address?: string;
  showSearchBox?: boolean;
  showDrawingManager?: boolean;
  isEditable?: boolean;
  showMarker?: boolean;
  onChange?: (e: MapChangeEvent) => void;
  selectedZone?: ZoneType;
  defaultMapZoom?: number;
  mapContainerStyle?: {
    height: string;
    minHeight?: string;
    maxWidth: string;
  };
}

export default function CardMapMultizones({
  className = '',
  label = '',
  value = { geozones: undefined, address: undefined, location: undefined },
  onChange = noop,
  resetBtn = false,
  edit = false,
  containerStyle = {
    height: CARD_MAP_HEIGHT,
    maxWidth: '100%',
  },
  selectedZone = GEOZONE_TYPE.PLANT as ZoneType,
  handleSelectedZoneChange = noop,
  errorMessage = '',
  missingGeozones = false,
  launchToEdit = noop,
  ...rest
}: CardMapMultizonesProps) {
  const [tooltipKey, setToolTipKey] = useState(uniqueId());
  type GeozoneKey = keyof Geozones;
  const hasPlantGeozone =
    value && value.geozones
      ? value.geozones[GEOZONE_TYPE.PLANT as GeozoneKey].zone.circle ||
        value.geozones[GEOZONE_TYPE.PLANT as GeozoneKey].zone.polygon
      : null;
  const disableDuplicateGeofence =
    !edit ||
    selectedZone === GEOZONE_TYPE.PLANT ||
    !hasPlantGeozone ||
    JSON.stringify(value.geozones![GEOZONE_TYPE.PLANT as GeozoneKey].zone) ===
      JSON.stringify(value.geozones![selectedZone as GeozoneKey].zone);
  const errorBorder = errorMessage.length > 0 && styles.redBorder;
  const border = errorMessage.length === 0 && styles.border;

  const geozoneLabels = {
    inqueue: 'In Queue',
    plant: 'Plant',
    return: 'Return',
  };

  return (
    <div className={cx(className, styles.map)}>
      {label && (
        <Label htmlFor="details-map" isRequired={!!edit}>
          {label}
        </Label>
      )}
      {missingGeozones && (
        <div className={styles.noGeozoneContainer}>
          <Label className={styles.noGeozoneLabel}>
            <i className={cx('icon-warning', styles.warningIcon)} />
            {PLANT_NO_GEOZONES}
          </Label>
          {PLANT_NO_ACTIVATION}
          <button
            className={cx('tt-btn', 'tt-btn-basic', styles.editPlantLink)}
            onClick={() => {
              launchToEdit();
            }}
            type="button"
          >
            <span>Edit Plant</span>
          </button>
        </div>
      )}
      <div className={cx(border, errorBorder)}>
        {resetBtn && (
          <Tabs
            value={selectedZone}
            classes={{
              root: styles.tabs,
            }}
            onChange={(_, newValue) => handleSelectedZoneChange(newValue)}
          >
            <Tab
              label={GEOZONE_TYPE.PLANT}
              value={GEOZONE_TYPE.PLANT}
              classes={{
                root: styles.tab,
              }}
              icon={
                <i
                  className={cx(
                    'icon-forward',
                    styles.marginRight,
                    styles.inlineStatusIcon,
                    styles.statusIconToJob,
                    styles.statusIconArrow,
                  )}
                />
              }
            />
            <Tab
              label="In Queue"
              value={GEOZONE_TYPE.INQUEUE}
              classes={{
                root: styles.tab,
              }}
              icon={
                <i
                  className={cx(
                    'icon-dot',
                    styles.marginRight,
                    styles.inlineStatusIcon,
                    styles.statusIconInQueue,
                    styles.statusIconDot,
                  )}
                />
              }
            />
            <Tab
              label={GEOZONE_TYPE.RETURN}
              value={GEOZONE_TYPE.RETURN}
              classes={{
                root: styles.tab,
              }}
              icon={
                <i
                  className={cx(
                    'icon-dot',
                    styles.marginRight,
                    styles.inlineStatusIcon,
                    styles.statusIconReturn,
                    styles.statusIconDot,
                  )}
                />
              }
            />
          </Tabs>
        )}
        <Map
          mapContainerStyle={containerStyle}
          {...value}
          onChange={onChange}
          isEditable={edit}
          selectedZone={selectedZone}
          {...rest}
        />
        {resetBtn && (
          <div className={cx(styles.zoneActionCt)}>
            <Button
              buttonClassName={cx('tt-btn', styles.clearGeozonesBtn, !edit && styles.btnDisabled)}
              iconClassName={cx('icon-clear-circle-outline')}
              name="Clear Geofence"
              dataTest="reset-geofence-btn"
              disabled={!edit}
              onClick={() => {
                const newGeozones = { ...value.geozones };
                newGeozones[selectedZone!]!.zone.circle = undefined;
                newGeozones[selectedZone!]!.zone.polygon = undefined;
                onChange({ geozones: newGeozones as Geozones });
              }}
            />
            {!disableDuplicateGeofence ? (
              <Tooltip
                key={tooltipKey}
                classes={{
                  popper: styles.dupPopper,
                  tooltip: styles.dupTooltip,
                }}
                text={`This will replace the current ${geozoneLabels[selectedZone]} geofence to match the Plant geofence.`}
              >
                <span className={styles.dupAcrossContainer}>
                  <Button
                    buttonClassName={cx(
                      'tt-btn',
                      styles.dupAcrossBtn,
                      disableDuplicateGeofence && styles.btnDisabled,
                    )}
                    iconClassName={cx('icon-content-copy')}
                    name="Duplicate Across Geofences"
                    dataTest="duplicate-across-geofences-btn"
                    disabled={disableDuplicateGeofence}
                    onClick={() => {
                      setToolTipKey(uniqueId());
                      const newGeozones = { ...value.geozones };
                      newGeozones[selectedZone!]!.zone = {
                        ...value.geozones![GEOZONE_TYPE.PLANT as GeozoneKey].zone,
                      };

                      onChange({ geozones: newGeozones as Geozones });
                    }}
                  />
                </span>
              </Tooltip>
            ) : (
              <Button
                buttonClassName={cx(
                  'tt-btn',
                  styles.dupAcrossBtn,
                  disableDuplicateGeofence && styles.btnDisabled,
                )}
                iconClassName={cx('icon-content-copy')}
                name="Duplicate Across Geofences"
                dataTest="duplicate-across-geofences-btn"
                disabled={disableDuplicateGeofence}
                onClick={() => {
                  setToolTipKey(uniqueId());
                  const newGeozones = { ...value.geozones };
                  newGeozones[selectedZone!]!.zone = {
                    ...value.geozones![GEOZONE_TYPE.PLANT as GeozoneKey].zone,
                  };

                  onChange({ geozones: newGeozones as Geozones });
                }}
              />
            )}
          </div>
        )}
      </div>
      {errorMessage.length > 0 && (
        <Label
          htmlFor="details-map-error-message"
          className={cx('tt-label--help', styles.error)}
          data-test="incomplete-geozones-input-error"
        >
          {errorMessage}
        </Label>
      )}
    </div>
  );
}

type GeozoneInfo = {
  zone: {
    circle?: {
      radius: number;
      center: Location;
    };
    polygon?: {
      points: Location[];
    };
  };
  name: string;
};

type Location = {
  latitude: number;
  longitude: number;
};

export interface CardMapMultizonesProps {
  handleSelectedZoneChange?: (zone: ZoneType) => void;
  className?: string;
  label?: string;
  missingGeozones?: boolean;
  value?: {
    geozones?: {
      plant: GeozoneInfo;
      inqueue: GeozoneInfo;
      return: GeozoneInfo;
    };
    address?: string;
    location?: Location;
  };
  onChange?: (e: MapChangeEvent) => void;
  launchToEdit?: () => void;
  resetBtn?: boolean;
  edit?: boolean;
  containerStyle?: {
    height: string;
    minHeight?: string;
    maxWidth: string;
  };
  selectedZone?: ZoneType;
  errorMessage?: string;
}
